import { useDebounce } from "use-debounce";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useSearchParams } from "react-router-dom";

import MovieCardSkeleton from "@/components/cards/MovieCard/MovieCard.skeleton.tsx";
import MoviesGrid from "@/components/helpers/MoviesGrid";
import { IconSadFace } from "@/components/icons";

import { useGetContent } from "@/hooks/api/content.hooks.ts";
import { cn } from "@/utils/cn";

const SearchContentPage = () => {
  const { t } = useTranslation();

  const [params] = useSearchParams();

  const [search] = useDebounce(params.get("search"), 1000);

  const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } = useGetContent({
    limit: 48,
    search,
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetching]);

  const flattenData = data ? data.pages.flatMap((page) => page.results) : null;
  return (
    <section className={"rounded-7.5 py-5 max-lg:rounded-5 max-lg:px-4 max-lg:py-6"}>
      <div
        className={
          "mb-2 flex items-center justify-between max-md:w-full max-md:flex-col max-md:items-stretch max-md:gap-6"
        }
      >
        <h1 className={"text-4xl font-semibold max-md:text-3xl/10"}>{t("Actions.movie-and-series")}</h1>
      </div>
      {flattenData && flattenData.length ? (
        <MoviesGrid content={flattenData} />
      ) : !isFetching ? (
        <div className={"flex flex-col items-center"}>
          <IconSadFace />
          <div className={"mt-4 max-w-[27.5rem] text-center"}>
            <h1 className={"text-2xl font-semibold"}>{t("error.nothing-found")}</h1>
            <p className={"text-on-surface mt-2 text-lg/6"}>{t("error.nothing-found-message")}</p>
          </div>
        </div>
      ) : null}
      {isFetching ? (
        <div
          className={cn("movies-grid mt-4", {
            "mt-8": !isFetchingNextPage,
          })}
        >
          {[...Array(12).keys()].map((_, index) => (
            <MovieCardSkeleton key={index} />
          ))}
        </div>
      ) : null}
      <div ref={ref} className={"h-px w-full"} />
    </section>
  );
};

export default SearchContentPage;
